import {
  CustomerSuitability,
  SUITABILITY_DESER,
  SUITABILITY_OPTIONS_LEVEL_CHANGE_PARSER,
  type CustomerSuitabilityOptionsLevelChange,
} from "../../tastyworks/customer"
import type HttpClient from "../../tastyworks/http"
import {
  parseItemsResponse,
  type ItemsResponse,
  type SingleResponse,
} from "../../tastyworks/response"
import { JsonBuilder } from "../../tastyworks/util/json"

const SUITABILITY_PARTIAL_VERSION = "20260910"

type SuitabilityVersion = typeof SUITABILITY_PARTIAL_VERSION

export class SuitabilityService {
  private readonly suitabilityClient: HttpClient

  constructor(customerMeClient: HttpClient) {
    this.suitabilityClient = customerMeClient.nested("suitability")
  }

  readonly show = async (): Promise<SingleResponse<CustomerSuitability>> =>
    this.suitabilityClient.show(
      new CustomerSuitability(),
      SUITABILITY_DESER.update
    )

  readonly create = async (
    suitability: CustomerSuitability,
    options: {
      version?: SuitabilityVersion
    } = {}
  ): Promise<SingleResponse<CustomerSuitability>> => {
    const headers = options.version
      ? { "Accept-Version": options.version }
      : undefined

    return this.suitabilityClient.create(
      suitability,
      SUITABILITY_DESER.serialize,
      SUITABILITY_DESER.update,
      undefined,
      undefined,
      headers
    )
  }

  readonly createPartial = async (
    suitability: Partial<CustomerSuitability>,
    options: {
      version?: SuitabilityVersion
    } = {}
  ): Promise<SingleResponse<CustomerSuitability>> => {
    const headers = options.version
      ? { "Accept-Version": options.version }
      : undefined

    return this.suitabilityClient.createPartial(
      suitability,
      SUITABILITY_DESER.serializePartial,
      SUITABILITY_DESER.toParser(),
      undefined,
      undefined,
      headers
    )
  }

  readonly update = async (
    suitability: CustomerSuitability,
    options: {
      version?: SuitabilityVersion
    } = {}
  ): Promise<SingleResponse<CustomerSuitability>> => {
    const headers = options.version
      ? { "Accept-Version": options.version }
      : undefined

    return this.suitabilityClient.update(
      suitability,
      SUITABILITY_DESER.serialize,
      SUITABILITY_DESER.update,
      undefined,
      undefined,
      headers
    )
  }

  readonly updatePartial = async (
    suitability: Partial<CustomerSuitability>,
    options: {
      version?: SuitabilityVersion
    } = {}
  ): Promise<SingleResponse<CustomerSuitability>> => {
    const headers = options.version
      ? { "Accept-Version": options.version }
      : undefined

    return this.suitabilityClient.updatePartial(
      suitability,
      SUITABILITY_DESER.serializePartial,
      SUITABILITY_DESER.toParser(),
      undefined,
      undefined,
      headers
    )
  }

  readonly calculateOptionsLevelChanges = async (
    suitability: CustomerSuitability
  ): Promise<ItemsResponse<CustomerSuitabilityOptionsLevelChange>> => {
    const builder = new JsonBuilder()
    SUITABILITY_DESER.serialize(suitability, builder)
    const helper = await this.suitabilityClient.postJson(
      "calculate-options-level-changes",
      builder.json
    )
    return parseItemsResponse<CustomerSuitabilityOptionsLevelChange>(
      helper,
      SUITABILITY_OPTIONS_LEVEL_CHANGE_PARSER
    )
  }
}
